/**
 * Which Operations menu entries a person sees — shared by the desktop and mobile sidebars.
 *
 * Infinity, Oct 2026 (Nipin sir): the pricing and operations teams do not use the customer list,
 * the Quotation Report or Booking Turnaround. Each entry now asks for its own permission — the
 * one its page asks for too. Customer data access is untouched: customer names on quotations,
 * job orders and tasks show without any of these.
 */
import { BarChart3, Building2, Scale, Timer, Truck, Users } from 'lucide-react'
import { useHasPermission } from '@/hooks/useHasPermission'
import { Permissions } from '@/lib/permissions'
import type { NavItemConfig } from './NavItem'

// Master Data items
const masterDataItems: NavItemConfig[] = [
  {
    label: 'Customers',
    href: '/customers',
    icon: Users,
  },
  {
    label: 'Service Providers',
    href: '/service-providers',
    icon: Building2,
  },
  {
    label: 'Carriers',
    href: '/carriers',
    icon: Truck,
  },
]

/** The Operations menu entries a person's permissions open. */
export interface OperationsMenuAccess {
  /** `customer-list:view` — the Customers entry (and Sales → Companies). */
  customerList: boolean
  quotationReport: boolean
  bookingTurnaround: boolean
  provisionalReport: boolean
}

export function useOperationsMenuAccess(): OperationsMenuAccess {
  return {
    customerList: useHasPermission(Permissions.CUSTOMER_LIST_VIEW),
    quotationReport: useHasPermission(Permissions.REPORT_QUOTATION),
    bookingTurnaround: useHasPermission(Permissions.REPORT_BOOKING_TURNAROUND),
    provisionalReport: useHasPermission(Permissions.REPORT_PROVISIONAL),
  }
}

/**
 * Master Data entries. Customers needs `customer-list:view` (Infinity, Oct 2026: not for the
 * pricing and operations teams). Customer names on quotes, jobs and tasks are unaffected.
 */
export function visibleMasterDataItems(access: OperationsMenuAccess): NavItemConfig[] {
  return masterDataItems.filter((item) => item.href !== '/customers' || access.customerList)
}

/**
 * Reports entries, each on the permission its page and data endpoints check. Quotation Report
 * and Booking Turnaround were shown to everyone until Oct 2026; Provisional Report always was gated.
 */
export function visibleReportItems(access: OperationsMenuAccess): NavItemConfig[] {
  const items: NavItemConfig[] = []
  if (access.quotationReport) items.push({ label: 'Quotation Report', href: '/reports/quotations', icon: BarChart3 })
  if (access.bookingTurnaround) items.push({ label: 'Booking Turnaround', href: '/reports/booking-turnaround', icon: Timer })
  if (access.provisionalReport) items.push({ label: 'Provisional Report', href: '/reports/provisional', icon: Scale })
  return items
}
